// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { StyledTheme } from "@toolkit/Framework/Types/StyledTheme";
import React from "react";
import styled, { css } from "styled-components";

interface StepForm {
    inputs: any[];
}

export interface Step {
    form: StepForm;
    label: string;
    description: string;
}

interface StepWithResults extends Step {
    complete: boolean;
    percentageComplete: number;
}
export interface StepsProps {
    steps: StepWithResults[];
    currentStep: number;
    setCurrentStep: (step: number) => void;
    title: string;
    vertical?: boolean;
}
interface StepStyles {
    wrapper?: InjectableCss;
    stepsContainer?: InjectableCss;
    step?: InjectableCss;
    line?: InjectableCss;
    stepLabel?: InjectableCss;
    description?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepTypography?: InjectableCss;
}

const stepStyles = (numberOfSteps: number, vertical = false): StepStyles => {
    const lineThickness = "2px";
    const linePercentage = 55 / numberOfSteps;
    return {
        stepTypography: {
            css: css`
                position: absolute;
                z-index: 2;
            `,
        },
        wrapper: {
            css: css`
                display: flex;
                justify-content: center;
                gap: 1.5rem;
                width: ${vertical ? "fit-content" : "100%"};
                flex-direction: ${vertical ? "column" : "row"};
            `,
        },
        description: {
            css: css`
                min-height: 3.5rem;
                margin: 0;
            `,
        },
        stepsContainer: {
            css: css`
                display: flex;
                justify-content: center;
                flex-direction: ${vertical ? "column" : "row"};
            `,
        },
        stepLabel: {
            css: css`
                position: absolute;
                color: ${getColor("primary")};
                white-space: nowrap;
                ${vertical ? "left: 60px;" : "top: -34px; left: 50%; transform: translateX(-50%);"}
            `,
        },
        stepWrapper: {
            css: css`
                display: flex;
                align-items: center;
                gap: 0.5rem;
                cursor: pointer;
            `,
        },
        line: {
            css: css`
                width: 40px;
                margin-inline: 15px;
                color: ${getColor("primary")};
                ${vertical ? "margin-inline: 0; margin-block: 15px; margin-left: 1.125rem;" : "margin-top: 1rem;"}
                height: ${vertical ? `calc(${linePercentage}vh - 3rem);` : lineThickness};
                width: ${vertical ? lineThickness : `calc(${linePercentage}vw - 2.5rem);`};
            `,
        },
    };
};

const getBackgroundColor = (active: boolean, percent: number) => {
    if (percent === 0) {
        return css`
            ${getColor("gray")}
        `;
    }
    if (active) {
        return css`
            ${getColor("primary")}
        `;
    }

    return css`
        ${getColor("secondary")}
    `;
};

const Step = styled.div<{ complete: boolean; current: boolean; percentageComplete: number }>`
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s ease-in-out;
    overflow: hidden;
    border: 1px solid ${props => getBackgroundColor(props.current, props.percentageComplete)};
    color: white;
    font-weight: 600;
    text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;
    position: relative;
`;

const StepLabel = styled.div<{ vertical?: boolean; current?: boolean; theme: StyledTheme }>`
    color: ${getColor("primary")};
    ${props => (props.vertical ? "left: 60px;" : "top: -34px; left: 50%; transform: translateX(-50%);")};
    ${props => props.current && "font-weight: 700;"}

    white-space: nowrap;
`;

const StyledLine = styled.div<{ css?: StyledProp; complete?: boolean }>`
    background-color: ${props =>
        props.complete ? props.theme.colors.secondary.main : props.theme.colors.primary.main};
    ${props => props.css}
`;

const getStepStyle = (complete: boolean, current: boolean, percentageComplete: number) => {
    return css`
        position: absolute;

        height: ${percentageComplete === 0 ? "100" : percentageComplete}%;
        width: 100%;
        background-color: ${getBackgroundColor(current, percentageComplete)};
        bottom: 0;
        left: 0;
    `;
};
const steps: React.FC<StepsProps> = ({ steps, currentStep, setCurrentStep, vertical }) => {
    const stepStylesInstance = stepStyles(steps.length, vertical);
    return (
        <StyledWrapper {...stepStylesInstance.wrapper}>
            <StyledWrapper {...stepStylesInstance.stepsContainer}>
                {steps.map((step, index) => {
                    const isCurrentStep = index === currentStep;
                    const { complete, percentageComplete, label } = step;
                    return (
                        <React.Fragment key={label + index}>
                            <StyledWrapper {...stepStylesInstance.stepWrapper} onClick={() => setCurrentStep(index)}>
                                <Step
                                    complete={complete}
                                    current={isCurrentStep}
                                    percentageComplete={percentageComplete}
                                >
                                    <Typography {...stepStylesInstance.stepTypography}>
                                        {complete ? "✓" : index + 1}
                                    </Typography>

                                    <StyledWrapper css={getStepStyle(complete, isCurrentStep, percentageComplete)} />
                                </Step>
                                <StepLabel vertical={vertical} current={isCurrentStep}>
                                    {translate(label)}
                                </StepLabel>
                            </StyledWrapper>

                            {index < steps?.length - 1 && (
                                <StyledLine {...stepStylesInstance.line} complete={complete} />
                            )}
                        </React.Fragment>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default steps;
